import React, { useState } from 'react';

function fmt(n) {
  if (n == null) return '—';
  return '$' + Math.round(n).toLocaleString();
}

function Row({ label, value, sub, highlight, strike, muted }) {
  return (
    <div className={[
      'flex items-start justify-between gap-3 py-2 border-b border-border last:border-0',
      highlight ? 'bg-success/5 -mx-4 px-4 rounded' : '',
    ].join(' ')}>
      <p className={['text-[12px] leading-snug', muted ? 'text-text-faint' : 'text-text-muted'].join(' ')}>
        {label}
        {sub && <span className="block text-[10px] text-text-faint mt-0.5">{sub}</span>}
      </p>
      <p className={[
        'text-[13px] font-semibold font-mono text-right flex-shrink-0',
        strike ? 'line-through text-danger/60' : '',
        highlight ? 'text-success' : '',
        muted ? 'text-text-faint font-normal' : 'text-text',
      ].join(' ')}>
        {value}
      </p>
    </div>
  );
}

function TypeCard({ type, amount }) {
  const a = Math.max(0, Number(amount) || 10000);

  let rows = [];
  let footer = null;
  let penaltyNote = null;
  let borderColor = 'border-border';
  let badgeColor = 'bg-bg-s3 text-text-muted';

  if (type === 'loan') {
    const receive = a;
    rows = [
      { label: 'You requested',                value: fmt(a) },
      { label: 'Vested balance change',        value: 'None', muted: true, sub: 'Loan is a receivable — the money stays in your account as a loan asset; only distributions reduce your balance' },
      { label: 'Federal withholding',          value: 'None', muted: true },
      { label: 'You receive in your bank',     value: fmt(receive), highlight: true },
      { label: '10% early withdrawal penalty', value: 'None', muted: true },
    ];
    footer = 'Loan is the cleanest option — no taxes, no penalty. Your vested balance stays the same because the loan is still your money (just in a different form). You repay with interest over up to 5 years. If you default, the outstanding balance becomes taxable income that year.';
    borderColor = 'border-blue-500/30';
    badgeColor = 'bg-blue-50 text-blue-700';
    penaltyNote = (
      <div className="border-t border-border px-4 py-3 space-y-3 bg-blue-500/5">
        <div>
          <p className="text-[11px] font-semibold text-text">How your loan headroom is calculated (IRC §72(p))</p>
          <p className="text-[11px] text-text-faint font-mono mt-1">
            Max you can borrow = min($50,000,  50% × vested balance) − outstanding loans
          </p>
          <p className="text-[10px] text-text-faint mt-0.5">Your vested balance never decreases when you take a loan — only outstanding loans reduce your headroom.</p>
        </div>

        <div className="space-y-1">
          <p className="text-[11px] font-semibold text-text-muted">Example — first loan</p>
          <div className="text-[11px] text-text-faint font-mono space-y-0.5 pl-2 border-l-2 border-blue-400/30">
            <p>Vested balance: $80,000 · Outstanding loans: $10,000</p>
            <p>Cap = min($50,000,  50% × $80,000) = min($50,000,  $40,000) = $40,000</p>
            <p>Headroom = $40,000 − $10,000 = <span className="text-blue-600 font-bold">$30,000 still available</span></p>
          </div>
        </div>

        <div className="space-y-1">
          <p className="text-[11px] font-semibold text-text-muted">Now takes a second loan of $15,000</p>
          <div className="text-[11px] text-text-faint font-mono space-y-0.5 pl-2 border-l-2 border-blue-400/30">
            <p>Vested balance: $80,000 (unchanged) · Loan 1: $10,000 · Loan 2: $15,000 = $25,000 total</p>
            <p>Cap = min($50,000,  50% × $80,000) = $40,000 (same — vested never changed)</p>
            <p>Headroom = $40,000 − $25,000 = <span className="text-blue-600 font-bold">$15,000 still available</span></p>
          </div>
        </div>

        <p className="text-[10px] text-text-faint">Primary residence loans may extend repayment to 15 years instead of 5.</p>
      </div>
    );
  } else if (type === 'hardship') {
    // Hardship: not an eligible rollover distribution → 10% optional withholding (default).
    // 10% early withdrawal penalty applies if under 59½.
    const withholding = Math.round(a * 0.10);
    const penalty = Math.round(a * 0.10);
    const receive = a - withholding;
    rows = [
      { label: 'You requested',                                         value: fmt(a) },
      { label: 'Vested balance decreases by',                           value: fmt(a) },
      { label: '10% federal withholding (default, can be waived)',      value: fmt(withholding) },
      { label: 'You receive in your bank',                              value: fmt(receive), highlight: true },
      { label: '10% early withdrawal penalty (if under 59½)',           value: fmt(penalty), sub: 'Owed at tax time — from your regular pocket, NOT the 401(k)' },
    ];
    footer = 'The vested balance only sees the $' + a.toLocaleString() + ' withdrawal. The $' + penalty.toLocaleString() + ' penalty is a separate bill on your 1040 — it comes from your own savings, not the plan.';
    borderColor = 'border-orange-500/30';
    badgeColor = 'bg-orange-50 text-orange-700';
    penaltyNote = (
      <div className="border-t border-border px-4 py-3 space-y-1.5 bg-orange-500/5">
        <p className="text-[11px] font-semibold text-text">How the 10% penalty flows</p>
        <div className="text-[11px] text-text-faint font-mono space-y-0.5">
          <p>Balance decreases by {fmt(a)} — 401(k) is done with this money</p>
          <p>{fmt(a)} splits: {fmt(receive)} to bank · {fmt(withholding)} IRS withholding</p>
          <p>April tax filing: {fmt(penalty)} penalty bill — paid from your own savings</p>
          <p>The penalty never touches the plan balance</p>
        </div>
      </div>
    );
  } else if (type === 'inservice') {
    const withholding = Math.round(a * 0.20);
    const receive = a - withholding;
    rows = [
      { label: 'You requested',                                    value: fmt(a) },
      { label: 'Vested balance decreases by',                      value: fmt(a) },
      { label: '20% mandatory federal withholding (IRC §3405)',    value: fmt(withholding) },
      { label: 'You receive in your bank',                         value: fmt(receive), highlight: true },
      { label: '10% early withdrawal penalty',                     value: 'None', muted: true, sub: 'Age 59½+ exempts you from the penalty' },
    ];
    footer = 'Available only at age 59½ or older while still employed. 20% is withheld and sent to the IRS; the rest goes to your bank. You may owe additional tax at filing if your marginal rate exceeds 20%.';
    borderColor = 'border-green-500/30';
    badgeColor = 'bg-green-50 text-green-700';
  } else if (type === 'separation_young') {
    const withholding = Math.round(a * 0.20);
    const penalty = Math.round(a * 0.10);
    const receive = a - withholding;
    rows = [
      { label: 'You requested',                                    value: fmt(a) },
      { label: 'Vested balance decreases by',                      value: fmt(a) },
      { label: '20% mandatory federal withholding (IRC §3405)',    value: fmt(withholding) },
      { label: 'You receive in your bank',                         value: fmt(receive), highlight: true },
      { label: '10% early withdrawal penalty (IRC §72(t))',        value: fmt(penalty), sub: 'Owed at tax time — comes from your regular savings, not the 401(k)' },
    ];
    footer = 'After separating from service before age 59½. The 401(k) balance only decreases by the requested amount. The penalty is settled when you file your 1040 — it never touches the plan.';
    borderColor = 'border-red-500/30';
    badgeColor = 'bg-red-50 text-red-700';
    penaltyNote = (
      <div className="border-t border-border px-4 py-3 space-y-1.5 bg-red-500/5">
        <p className="text-[11px] font-semibold text-text">How the 10% penalty flows</p>
        <div className="text-[11px] text-text-faint font-mono space-y-0.5">
          <p>Balance decreases by {fmt(a)} — 401(k) is done with this money</p>
          <p>{fmt(a)} splits: {fmt(receive)} to bank · {fmt(withholding)} IRS withholding</p>
          <p>April tax filing: {fmt(penalty)} penalty bill — paid from your own savings</p>
          <p>The penalty never touches the plan balance</p>
        </div>
      </div>
    );
  } else if (type === 'separation_old') {
    const withholding = Math.round(a * 0.20);
    const receive = a - withholding;
    rows = [
      { label: 'You requested',                                    value: fmt(a) },
      { label: 'Vested balance decreases by',                      value: fmt(a) },
      { label: '20% mandatory federal withholding (IRC §3405)',    value: fmt(withholding) },
      { label: 'You receive in your bank',                         value: fmt(receive), highlight: true },
      { label: '10% early withdrawal penalty',                     value: 'None', muted: true, sub: 'Age 59½+ (or Rule of 55: separated at age 55+) exempts you' },
    ];
    footer = 'Age 59½ or older at separation, or you separated from this employer in or after the year you turned 55 (Rule of 55). No early withdrawal penalty applies.';
    borderColor = 'border-amber-500/30';
    badgeColor = 'bg-amber-50 text-amber-700';
  } else if (type === 'rmd') {
    const withholding = Math.round(a * 0.20);
    const receive = a - withholding;
    const excise = Math.round(a * 0.25);
    rows = [
      { label: 'RMD amount (calculated by IRS tables)',            value: fmt(a) },
      { label: 'Vested balance decreases by',                      value: fmt(a) },
      { label: '20% mandatory federal withholding',                value: fmt(withholding) },
      { label: 'You receive in your bank',                         value: fmt(receive), highlight: true },
      { label: '10% early withdrawal penalty',                     value: 'None', muted: true, sub: 'Age 73+ exempts you automatically' },
      { label: '25% excise tax if you FAIL to take the RMD',       value: fmt(excise), sub: 'IRC §4974 — assessed on the amount you should have withdrawn but did not' },
    ];
    footer = 'RMDs begin at age 73 (SECURE 2.0). The 25% excise applies only if you skip the RMD entirely — it is computed on the shortfall, not the full balance.';
    borderColor = 'border-purple-500/30';
    badgeColor = 'bg-purple-50 text-purple-700';
    penaltyNote = (
      <div className="border-t border-border px-4 py-3 space-y-1.5 bg-purple-500/5">
        <p className="text-[11px] font-semibold text-text">If you skip the RMD (IRC §4974)</p>
        <div className="text-[11px] text-text-faint font-mono space-y-0.5">
          <p>Required amount NOT taken: {fmt(a)}</p>
          <p>25% excise assessed: {fmt(excise)} — paid from your own savings</p>
          <p>This is NOT a plan deduction — it comes from outside the account</p>
          <p>SECURE 2.0 reduced this from 50% to 25%, effective 2023</p>
        </div>
      </div>
    );
  }

  const TITLES = {
    loan:            'Plan Loan',
    hardship:        'Hardship Distribution',
    inservice:       'In-Service Distribution (59½+)',
    separation_young:'Separation — Under 59½',
    separation_old:  'Separation — Age 59½+',
    rmd:             'Required Minimum Distribution',
  };

  return (
    <div className={['rounded-xl border bg-bg overflow-hidden', borderColor].join(' ')}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-bg-s2">
        <p className="text-[13px] font-semibold text-text">{TITLES[type]}</p>
        <span className={['text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full', badgeColor].join(' ')}>
          {type === 'loan' ? 'No tax' : type === 'rmd' ? 'Age 73+' : type === 'inservice' ? 'Age 59½+' : type === 'separation_old' ? 'Age 59½+' : 'Taxable'}
        </span>
      </div>
      <div className="px-4 py-2">
        {rows.map((r, i) => (
          <Row key={i} {...r} />
        ))}
      </div>
      {footer && (
        <div className="px-4 pb-3">
          <p className="text-[11px] text-text-faint leading-relaxed">{footer}</p>
        </div>
      )}
      {penaltyNote}
    </div>
  );
}

const ALL_TYPES = ['loan', 'hardship', 'inservice', 'separation_young', 'separation_old', 'rmd'];

export default function TaxPenaltyGuide() {
  const [amount, setAmount] = useState('10000');
  const [selected, setSelected] = useState(ALL_TYPES);

  const LABELS = {
    loan:            'Loan',
    hardship:        'Hardship',
    inservice:       'In-Service',
    separation_young:'Separation <59½',
    separation_old:  'Separation 59½+',
    rmd:             'RMD',
  };

  function toggle(key) {
    setSelected(prev =>
      prev.includes(key)
        ? prev.length > 1 ? prev.filter(k => k !== key) : prev
        : [...prev, key]
    );
  }

  return (
    <div className="card p-5 space-y-5">
      {/* Header */}
      <div>
        <h3 className="text-[13px] font-semibold text-text">Tax &amp; Penalty — How Money Flows</h3>
        <p className="text-xs text-text-muted mt-0.5">
          Enter any amount to see exactly what leaves the plan, what reaches your bank, and what you owe at tax time.
        </p>
      </div>

      {/* Amount input */}
      <div className="flex items-center gap-3">
        <label className="text-[11px] font-mono uppercase tracking-widest text-text-faint flex-shrink-0">
          Example amount
        </label>
        <div className="flex items-center gap-1">
          <span className="text-sm text-text-muted font-bold">$</span>
          <input
            type="number"
            min={1}
            step={1000}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="input-field w-32 text-right font-mono font-semibold py-1.5 text-sm"
          />
        </div>
      </div>

      {/* Type filter chips */}
      <div className="flex flex-wrap gap-1.5">
        {ALL_TYPES.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => toggle(key)}
            className={[
              'px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors',
              selected.includes(key)
                ? 'bg-accent text-white border-accent'
                : 'bg-bg-s2 text-text-muted border-border hover:border-accent/40',
            ].join(' ')}
          >
            {LABELS[key]}
          </button>
        ))}
      </div>

      {/* Cards */}
      <div className="space-y-3">
        {ALL_TYPES.filter((k) => selected.includes(k)).map((type) => (
          <TypeCard key={type} type={type} amount={amount} />
        ))}
      </div>

      <p className="text-[10px] text-text-faint text-center font-mono">
        IRC §72(t) · IRC §3405 · IRC §4974 · SECURE 2.0 Act §107 · State taxes not shown
      </p>
    </div>
  );
}
